import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import React, { useCallback } from 'react'

import MultiSelectItem from 'components/form-fields/v5/MultiSelectItem'
import StyledAutocomplete from 'components/form-fields/v5/StyledAutocomplete'
import StyledTextField from 'components/form-fields/v5/StyledTextField'

import type {
  AutocompleteRenderGetTagProps,
  AutocompleteRenderInputParams,
  AutocompleteRenderOptionState,
} from '@mui/material/Autocomplete'
import type { SxProps, Theme } from '@mui/material/styles'

export type ZoneOption = { id: string; name: string }

type GeoZoneAutocompleteProps = {
  label: string
  options: ZoneOption[]
  value: ZoneOption[]
  onChange?: (selected: ZoneOption[]) => void
  // The list still opens, so the user sees every zone, but nothing changes.
  viewOnly?: boolean
  loading?: boolean
  popperSx?: SxProps<Theme>
}

type OptionProps = React.HTMLAttributes<HTMLLIElement> & { key: React.Key }

const getOptionLabel = (option: ZoneOption) => option.name
const isOptionEqualToValue = (option: ZoneOption, selected: ZoneOption) => option.id === selected.id
const FIRST_CHIP_SX = { maxWidth: '65%' }
const SPINNER_SX = { mr: 1, flexShrink: 0 }

const renderOption = ({ key, ...props }: OptionProps, option: ZoneOption, { selected }: AutocompleteRenderOptionState) => (
  <MultiSelectItem key={key} {...props} selected={selected}>
    {option.name}
  </MultiSelectItem>
)

// No checkbox where nothing can be unchecked; the list only names the zones.
const renderViewOnlyOption = ({ key, ...props }: OptionProps, option: ZoneOption) => (
  <MenuItem key={key} {...props}>
    <Typography noWrap>{option.name}</Typography>
  </MenuItem>
)

const GeoZoneAutocomplete = ({
  label,
  options,
  value,
  onChange,
  viewOnly = false,
  loading = false,
  popperSx,
}: GeoZoneAutocompleteProps) => {
  const handleChange = useCallback(
    (_event: React.SyntheticEvent, selected: ZoneOption[]) => {
      if (!viewOnly) onChange?.(selected)
    },
    [viewOnly, onChange]
  )

  // The spinner the route dropdowns use, ahead of the chips.
  const renderInput = useCallback(
    (params: AutocompleteRenderInputParams) => {
      // A read-only input still opens the list on click; MUI's own readOnly would keep it shut.
      const fieldParams = viewOnly ? { ...params, inputProps: { ...params.inputProps, readOnly: true } } : params
      return (
        <StyledTextField
          {...fieldParams}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                {loading && <CircularProgress size={20} sx={SPINNER_SX} />}
                {params.InputProps.startAdornment}
              </>
            ),
          }}
          label={label}
        />
      )
    },
    [label, loading, viewOnly]
  )

  // First chip plus a "+N" count (the app's multi-select convention), so the field never grows past 40px.
  const renderTags = useCallback(
    (selected: ZoneOption[], getTagProps: AutocompleteRenderGetTagProps) => {
      const [first, ...rest] = selected
      if (!first) return null
      const { key, onDelete, ...firstTagProps } = getTagProps({ index: 0 })
      return (
        <>
          <Chip
            key={key}
            {...firstTagProps}
            label={first.name}
            size="small"
            sx={FIRST_CHIP_SX}
            onDelete={viewOnly ? undefined : onDelete}
          />
          {rest.length > 0 && <Chip label={`+${rest.length}`} size="small" />}
        </>
      )
    },
    [viewOnly]
  )

  return (
    <StyledAutocomplete
      disableCloseOnSelect
      fullWidth
      multiple
      disableClearable={viewOnly}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      loading={loading}
      options={options}
      renderInput={renderInput}
      renderOption={viewOnly ? renderViewOnlyOption : renderOption}
      renderTags={renderTags}
      slotProps={{ popper: { sx: popperSx } }}
      value={value}
      onChange={handleChange}
    />
  )
}

export default GeoZoneAutocomplete
